'use client'

import { cn } from '@mntn-dev/ui-utilities'
import type { PropsWithChildren, ReactNode } from 'react'

import {
  elevationMap,
  themeBackgroundMap,
  themeBorderColorMap,
} from '@mntn-dev/ui-theme'

import { motion } from 'motion/react'
import { getTestProps } from '../../utils'
import {
  CardProvider,
  type UseCardProps,
  useCard,
  useCardContext,
} from './use-card.ts'

export type CardProps = Readonly<
  UseCardProps & {
    as?: 'div' | 'li'
    children: ReactNode
    className?: string
    onClick?: () => void
  }
>

export const Card = ({
  as,
  children,
  className,
  ...useCardProps
}: CardProps) => {
  const Component = as ?? 'div'
  const context = useCard(useCardProps)

  const { dataTestId, dataTrackingId } = context

  return (
    <CardProvider value={context}>
      <Component
        className={cn(
          'w-full flex rounded-lg text-left p-4',
          themeBackgroundMap['container-secondary'],
          themeBorderColorMap.muted,
          elevationMap.xs,
          className
        )}
        {...getTestProps({ dataTestId, dataTrackingId })}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="w-full"
        >
          {children}
        </motion.div>
      </Component>
    </CardProvider>
  )
}

export const CardContent = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => {
  const { orientation } = useCardContext()
  return (
    <div
      className={cn(
        'flex gap-4',
        orientation === 'vertical' && 'flex-col',
        className
      )}
    >
      {children}
    </div>
  )
}
